import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom'
import { _beginformState as _storage, formOptions } from '../_states/_beginformState';
import { _resetState } from '../_states/_resetState';
import ErrorMessage from '../helpers/_ErrorMessage';
import { getUtrn } from '../Services/_services';
import { yupResolver } from '@hookform/resolvers/yup';

function BeginForm(props) {
  const navigate = useNavigate();
  const { control, register, handleSubmit, formState: { errors } } = useForm({mode:"onTouched", resolver: yupResolver(formOptions)})

  const [confirm, setConfirm] = useState(_storage.getConfirm());
  const [utrn, setUtrn] = useState(_storage.getUtrn());

  // scroll to the top
  useEffect(() => {
    window.scrollTo(0, 0);

	if(utrn === '') {
		getUtrn().then(result => {
			if(result?.returnCode === 200 && result?.returnValue && result?.returnValue !== '') {
				setUtrn(result.returnValue);
				_storage.setUtrn(result.returnValue);
			} else {
				navigate('/error');
			}
		})
		.catch(error => {
			navigate('/error');
		});
	}
  }, [])

  const onSubmit = event => {
    _resetState.setResetState(false);
    navigate('/aboutyou');
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <main id="main" className="sectionOne">
			<div className="container-fluid">
            	<div className="row">
					<div className="text">
						<h1 className="heading-large">Professionals: Make a social care referral for adults</h1>

						<p>This form has 4 pages and will approximately take 10 minutes to complete.</p>

						<h2>Before you begin</h2>
							<div>
								<h3>What is an Adult Social Care referral?</h3>
								<p>When you make a request for social care services to Leeds City Council we call it a 'referral'. Online referrals should not be used in emergency situations as there may be a delay in the referral being processed.
								If this is a safeguarding referral, <a href="https://www.leeds.gov.uk/adult-social-care/worried-about-someone/report-neglect-or-abuse-of-an-adult" target="_blank" rel="noreferrer">visit the safeguarding page on our website (opens in new tab)</a></p>
								<p>You can use this form to create a new referral if you are worried or concerned about an adult who needs support:</p>

								<ul className="list list-bullet">
									<li>With personal care such as washing, dressing and toileting</li>
									<li>To stay safe around the home including equipment and alarms</li>
									<li>To access the community, leisure or work</li>
									<li>To maintain relationships</li>
									<li>Support for unpaid carers</li>
								</ul>

								<p>Make sure you have spoken to the person to ensure they want help from us. We may not be able to help someone who does not want it.</p>
							</div>

							<details className="accordion">
								<summary>
									Please read the following information before making a referral
									<svg height="11" viewBox="0 0 16 11" width="16"
										xmlns="http://www.w3.org/2000/svg">
										<path
											d="m3.20367261 7.49899519 6.34871411-5.29024783c.52758388-.44314909.59747518-1.22949597.1563193-1.75874764-.44115592-.52925167-1.22723358-.60210855-1.7581455-.16295208l-7.50125472 6.25107685c-.28474092.23789197-.4493058.58982123-.4493058.96086028s.16456488.72296831.4493058.96086027l7.50125472 6.25105606c.53074839.4413231 1.31866588.3693145 1.76062396-.1609053.44195812-.5302198.37089262-1.3182229-.15879776-1.7608153z"
											fill="#003f54" transform="matrix(0 -1 1 0 .50062 10.50062)" />
									</svg>
								</summary>

								<div className="accordion__inner">
									<p>For urgent support in relation to a person's mental health contact the Single Point of Access (Crisis team) <strong>0300 300 1485.</strong></p>
									<p>If you are concerned about someone's long term mental health needs, please refer to their GP unless there are other care and support needs or you feel they are at significant risk.</p>
									<p>If you or someone you know are in immediate danger please contact the police on <strong>999</strong>.</p>
									<p>For housing needs, homelessness or risk of homelessness, please <a href="https://www.leeds.gov.uk/housing/homeless-or-at-risk/contact-leeds-housing-options" target="_blank" rel="noreferrer">visit the housing needs page on our website (opens in new tab)</a></p>
									<p>For housing repairs, please refer to the local housing office for local authority tenants or for home owners access the <a href="https://www.leedsdirectory.org/" target="_blank" rel="noreferrer">Leeds Directory website (opens in new tab)</a> or call the Leeds Directory helpline on <strong>0113 378 4610.</strong></p>
									<p>If you are concerned about domestic abuse and the person does not have a care and support need, please <a href="https://ldvs.uk/" target="_blank" rel="noreferrer">visit the Leeds Domestic Violence Service website (opens in new tab)</a></p>
									<p>If you have a safeguarding concern and you are worried that an adult is being abused or neglected, please <a href="https://www.leeds.gov.uk/adult-social-care/worried-about-someone/report-neglect-or-abuse-of-an-adult" target="_blank" rel="noreferrer">visit the safeguarding page on our website (opens in new tab)</a></p>
									<p>If the main presenting concern is around drug or alcohol misuse and there are no care and support needs or risks identified, please <a href="https://www.forwardleeds.co.uk/" target="_blank" rel="noreferrer">visit the Forward Leeds website (opens in new tab)</a></p>
									<p>Out of Hours: for any urgent situations that cannot wait for office hours, contact the Adult social care Emergency Duty Team on <strong>0113 378 0644.</strong></p>
									<p>If you have a query or concern about an existing referral please get in touch on <b>0113 222 4401.</b> Our working hours are Monday to Thursday, 9:00 AM until 5:00 PM and Friday from 9:00 AM until 4:30 PM except for Bank Holidays.</p>
								</div>
							</details>

						<h3>Privacy Notice</h3>
						<p>For information about how we will use and process your data, please read the following:</p>
						<p><a href="https://www.leeds.gov.uk/Pages/Privacy-and-Data.aspx" target="_blank" rel="noreferrer">Third party social care privacy notice (opens in new tab)</a></p>

						<div className="text">
							<div className="notice">
								<i className="fa fa-exclamation-triangle" aria-hidden="true">
									<span className="sr-only">Warning</span>
								</i>
								<strong className="bold-small">Please note that if you refresh the browser while completing this form, you will lose all data you have input and will be navigated back to this page.</strong>
							</div>
						</div>

						<p>If you are happy to proceed, check the box at the bottom of the page.</p>
						<div className={errors.confirm ? 'form-group form-group--error' : 'form-group'}>
							<ErrorMessage id="confirm" error={errors.confirm}/>
							<fieldset>
								<Controller name="confirm" control={control}
									render={({ field: { value, onChange, ...field } }) => (
										<label htmlFor="confirm" className="block-label">
										<input type="checkbox" htmlFor="confirm"
											className={`block-label ${errors.confirm ? 'is-invalid' : ''}`}
											aria-invalid={errors.confirm ? "true" : "false"}
											aria-describedby={errors.confirm ? "confirm-error" : ""}
											{...register('confirm')} id="confirm" name='confirm' value={confirm} checked={confirm}
											onChange={(event) => {
												onChange(event.target.checked);
												setConfirm(event.target.checked);
												_storage.setConfirm(event.target.checked);
											}}
										/>
										<span htmlFor="confirm">I have read and accept the privacy notice </span>
									</label>
									)}
								/>
							</fieldset>
						</div>

						<div className="form-group">
							<button className="btn btn-primary btn-arrow" type="submit" value="Start now">Begin this form</button>
						</div>
					</div>
				</div>
          	</div>
        </main>
      </form>
    </>
  );
};
export default BeginForm;
